// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — FinancialRadarPage
// Detects leaks across:
//   • Inventory gaps (with operational buffers applied)
//   • Labor inefficiency (shift-level correlation of wages vs revenue)
// Tabs: All / Inventory / Labor
// ══════════════════════════════════════════════════════════════════════════════

import { useCallback, useMemo, useState } from 'react';
import {
  Radar, AlertTriangle, AlertCircle, TrendingDown, CheckCircle2,
  Users, Package, Shield
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { supabase } from '@lib/supabase';
import { PageHeader } from '@components/ui/PageHeader';
import { Grid } from '@components/ui/Primitives';
import { StatCard } from '@components/ui/StatCard';
import { EmptyState } from '@components/ui/States';
import { GapCard } from '@components/financial-radar/GapCard';

export function FinancialRadarPage({ data, refresh }) {
  const { theme } = useTheme();
  const { t, lang } = useLang();
  const [activeTab, setActiveTab] = useState('all');

  const allGaps = data?.gaps || [];
  const laborKpis = data?.laborKpis || {
    total_monthly_labor_cost: 0,
    total_monthly_waste: 0,
    unprofitable_shifts: 0,
    healthy_shifts: 0,
    avg_labor_ratio: 0
  };

  // Separate inventory vs labor gaps
  const inventoryGaps = useMemo(
    () => allGaps.filter(g => g.gap_type !== 'labor_inefficiency'),
    [allGaps]
  );
  const laborGaps = useMemo(
    () => allGaps.filter(g => g.gap_type === 'labor_inefficiency'),
    [allGaps]
  );

  const visibleGaps = useMemo(() => {
    if (activeTab === 'inventory') return inventoryGaps;
    if (activeTab === 'labor') return laborGaps;
    return allGaps;
  }, [activeTab, allGaps, inventoryGaps, laborGaps]);

  const handleResolve = useCallback(async (id) => {
    // Labor gaps are live-computed (not DB rows) — just refetch to let them recompute
    if (String(id).startsWith('labor_gap_')) {
      await refresh?.();
      return;
    }
    const { error } = await supabase
      .from('financial_gaps')
      .update({ is_resolved: true, resolved_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) refresh?.();
  }, [refresh]);

  // Empty state
  if (allGaps.length === 0) {
    return (
      <div>
        <PageHeader
          icon={<Radar size={22} color={theme.primary} />}
          title={t.financialRadarTitle}
          description={t.financialRadarDesc}
        />
        <EmptyState
          icon={<CheckCircle2 size={36} color={theme.success} />}
          title={t.noGaps}
          description={t.noGapsDesc}
        />
      </div>
    );
  }

  const totalLoss = allGaps.reduce(
    (s, g) => s + parseFloat(g.estimated_monetary_loss || 0),
    0
  );
  const criticalCount = allGaps.filter(g => g.severity === 'critical').length;
  const bufferAppliedCount = allGaps.filter(g => g.buffer_applied).length;

  const tabs = [
    { key: 'all', label: lang === 'ar' ? 'الكل' : 'All', icon: Radar, count: allGaps.length },
    { key: 'inventory', label: t.gapInventory, icon: Package, count: inventoryGaps.length },
    { key: 'labor', label: lang === 'ar' ? 'عمالة' : 'Labor', icon: Users, count: laborGaps.length }
  ];

  return (
    <div>
      <PageHeader
        icon={<Radar size={22} color={theme.primary} />}
        title={t.financialRadarTitle}
        description={t.financialRadarDesc}
      />

      {/* Top KPIs */}
      <div style={{ marginBottom: 24 }}>
        <Grid min={220}>
          <StatCard
            icon={<AlertTriangle size={20} color={theme.danger} />}
            label={t.activeGaps}
            value={allGaps.length}
            accentColor={theme.danger}
          />
          <StatCard
            icon={<AlertCircle size={20} color={theme.warning} />}
            label={t.severity_critical}
            value={criticalCount}
            accentColor={theme.warning}
          />
          <StatCard
            icon={<TrendingDown size={20} color={theme.danger} />}
            label={t.estimatedLoss}
            value={`${t.sar} ${totalLoss.toFixed(0)}`}
            subtext={lang === 'ar' ? 'شهرياً' : 'per month'}
            accentColor={theme.danger}
          />
          <StatCard
            icon={<Shield size={20} color={theme.success} />}
            label={t.operationalBuffer}
            value={bufferAppliedCount}
            subtext={lang === 'ar' ? 'فجوة معدّلة' : 'gaps adjusted'}
            accentColor={theme.success}
          />
        </Grid>
      </div>

      {/* Labor KPIs */}
      {laborGaps.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{
            margin: '0 0 12px',
            fontSize: 13,
            fontWeight: 700,
            color: theme.textSecondary,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <Users size={14} />
            {t.laborEfficiency}
          </h4>
          <Grid min={200}>
            <StatCard
              icon={<Users size={20} color={theme.info} />}
              label={t.laborCost}
              value={`${t.sar} ${laborKpis.total_monthly_labor_cost.toLocaleString()}`}
              subtext={lang === 'ar' ? 'شهرياً' : 'per month'}
              accentColor={theme.info}
            />
            <StatCard
              icon={<AlertTriangle size={20} color={theme.danger} />}
              label={t.unprofitableShift}
              value={laborKpis.unprofitable_shifts}
              subtext={`${laborKpis.healthy_shifts} ${lang === 'ar' ? 'صحية' : 'healthy'}`}
              accentColor={theme.danger}
            />
            <StatCard
              icon={<TrendingDown size={20} color={theme.danger} />}
              label={lang === 'ar' ? 'هدر العمالة' : 'Labor Waste'}
              value={`${t.sar} ${laborKpis.total_monthly_waste.toLocaleString()}`}
              accentColor={theme.danger}
            />
          </Grid>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 20,
        overflowX: 'auto',
        paddingBottom: 4
      }}>
        {tabs.map(tab => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <div
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                background: isActive ? theme.primaryDim : theme.bgInput,
                color: isActive ? theme.primary : theme.textSecondary,
                border: `1px solid ${isActive ? theme.primaryBorder : theme.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap'
              }}
            >
              <TabIcon size={12} />
              {tab.label}
              <span style={{
                background: isActive ? theme.primary : theme.textTertiary,
                color: '#fff',
                borderRadius: 10,
                padding: '1px 7px',
                fontSize: 10,
                fontWeight: 700
              }}>
                {tab.count}
              </span>
            </div>
          );
        })}
      </div>

      {/* Gap list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {visibleGaps.map(gap => (
          <GapCard key={gap.id} gap={gap} onResolve={handleResolve} />
        ))}
      </div>
    </div>
  );
}
